/**
 * Package API Service
 * Handles remote package operations using pre-signed URL API
 * No credentials stored locally - uses bearer token auth only
 */

import { loadRemotePackageApiConfig } from '../utils/envConfig';

class PackageApiService {
  constructor() {
    this.endpoint = '';
    this.bearerToken = '';
    this.isConfigured = false;
    this.lastSync = null;
    this.isConnected = false;
    this.error = null;
    this.envConfig = null;
    this.isEndpointManaged = false;
    this.isTokenManaged = false;
    
    // Load configuration from environment variables
    this.loadFromEnvironment();
  }

  /**
   * Load configuration from environment variables
   */
  loadFromEnvironment() {
    this.envConfig = loadRemotePackageApiConfig();

    if (process.env.NODE_ENV === 'development') {
      console.info('[PackageApiService] loadFromEnvironment()', {
        env: {
          hasEndpoint: this.envConfig?.hasEndpoint || false,
          hasToken: this.envConfig?.hasToken || false,
          isEndpointValid: this.envConfig?.isEndpointValid || false,
          isConfigured: this.envConfig?.isConfigured || false,
          errors: this.envConfig?.errors || []
        }
      });
    }
    
    if (this.envConfig.isConfigured) {
      this.endpoint = this.envConfig.endpoint.replace(/\/$/, ''); // Remove trailing slash
      this.bearerToken = this.envConfig.token;
      this.isConfigured = true;
      this.isEndpointManaged = this.envConfig.hasEndpoint;
      this.isTokenManaged = this.envConfig.hasToken;
      
      // Store any validation errors
      if (this.envConfig.errors.length > 0) {
        this.error = this.envConfig.errors.join('; ');
      }

      if (process.env.NODE_ENV === 'development') {
        console.info('[PackageApiService] Environment config applied', {
          endpointPreview: this.endpoint ? `${this.endpoint.slice(0, 40)}...` : '',
          tokenLength: this.bearerToken ? this.bearerToken.length : 0,
          isEndpointManaged: this.isEndpointManaged,
          isTokenManaged: this.isTokenManaged
        });
      }
    }
  }

  /**
   * Configure remote package API
   * @param {string} endpoint - API endpoint URL
   * @param {string} bearerToken - Bearer token for authentication
   */
  configure(endpoint, bearerToken) {
    // Only allow manual configuration if not managed by environment
    if (!this.isEndpointManaged) {
      this.endpoint = (endpoint || '').trim().replace(/\/$/, ''); // Remove trailing slash
    }
    
    if (!this.isTokenManaged) {
      this.bearerToken = (bearerToken || '').trim();
    }
    
    this.isConfigured = !!(this.endpoint && this.bearerToken);
    this.error = null;
    this.isConnected = false;
  }

  /**
   * Get environment configuration status
   * @returns {Object} - Environment configuration information
   */
  getEnvironmentConfig() {
    return {
      hasEndpoint: this.envConfig?.hasEndpoint || false,
      hasToken: this.envConfig?.hasToken || false,
      isEndpointValid: this.envConfig?.isEndpointValid || false,
      isConfigured: this.envConfig?.isConfigured || false,
      errors: this.envConfig?.errors || [],
      isEndpointManaged: this.isEndpointManaged,
      isTokenManaged: this.isTokenManaged
    };
  }

  /**
   * Get authorization headers
   * @returns {Object} - Auth headers
   */
  getAuthHeaders() {
    return {
      'Authorization': `Bearer ${this.bearerToken}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Normalize package payload into common structure
   * @param {Object} pkg - Package payload
   * @returns {Object} - Normalized package metadata
   */
  normalizePackage(pkg) {
    const name = pkg.name || pkg.packageName || pkg.key || pkg.id;
    const created = pkg.created || pkg.createdAt || pkg.lastModified;

    return {
      id: pkg.id || name,
      name,
      version: pkg.version || 'unknown',
      created: created ? new Date(created) : new Date(),
      size: pkg.size || 0,
      fileCount: pkg.fileCount || 0,
      config: pkg.config || {
        name,
        version: pkg.version || 'unknown',
        description: '',
        includeTextures: true,
        includeModels: true,
        includeHistory: true,
        compressionLevel: 6,
        selectedFiles: []
      },
      source: 'remote',
      syncStatus: pkg.syncStatus || 'remote-only',
      checksum: pkg.checksum
    };
  }

  /**
   * Normalize pre-signed upload response variants
   * @param {Object} data - API response payload
   * @returns {Object} - Normalized upload contract
   */
  normalizeUploadContract(data) {
    const uploadUrl =
      data.uploadUrl ||
      data.url ||
      data.presignedUrl ||
      data.presignUrl ||
      data?.upload?.url;

    if (!uploadUrl) {
      throw new Error('Upload URL missing from presigned upload response.');
    }

    return {
      uploadUrl,
      method: (data.method || data.uploadMethod || 'PUT').toUpperCase(),
      headers: data.headers || {},
      fields: data.fields || null,
      expiresAt: data.expiresAt || data.expiry || null,
      objectKey: data.objectKey || data.key || null
    };
  }

  /**
   * Normalize pre-signed download response variants
   * @param {Object} data - API response payload
   * @returns {Object} - Normalized download contract
   */
  normalizeDownloadContract(data) {
    const downloadUrl =
      data.downloadUrl ||
      data.url ||
      data.presignedUrl ||
      data.presignUrl ||
      data?.download?.url;

    if (!downloadUrl) {
      throw new Error('Download URL missing from presigned download response.');
    }

    return {
      downloadUrl,
      expiresAt: data.expiresAt || data.expiry || null,
      headers: data.headers || {}
    };
  }

  /**
   * List remote packages
   * @returns {Promise<Array>} - Array of remote package info
   */
  async listPackages() {
    if (!this.isConfigured) {
      throw new Error('Package API is not configured. Please configure endpoint and bearer token.');
    }

    try {
      const response = await fetch(`${this.endpoint}/packages`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please check your bearer token.');
        } else if (response.status === 403) {
          throw new Error('Access denied. You do not have permission to list packages.');
        } else {
          throw new Error(`Failed to list packages: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      this.lastSync = new Date();
      this.isConnected = true;
      this.error = null;

      return Array.isArray(data)
        ? data.map(pkg => this.normalizePackage(pkg))
        : [];
    } catch (error) {
      this.isConnected = false;
      this.error = error.message || 'Failed to list packages';
      throw error;
    }
  }

  /**
   * Request presigned upload URL
   * @param {string} packageName - Name of the package
   * @param {string} packageSize - Size of the package in bytes
   * @param {string} contentType - Content type of the package
   * @returns {Promise<Object>} - Presigned upload URL and metadata
   */
  async requestPresignedUpload(packageName, packageSize, contentType = 'application/zip') {
    if (!this.isConfigured) {
      throw new Error('Package API is not configured. Please configure endpoint and bearer token.');
    }

    try {
      const response = await fetch(`${this.endpoint}/packages/presign-upload`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          packageName,
          packageSize,
          contentType
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please check your bearer token.');
        } else if (response.status === 403) {
          throw new Error('Access denied. You do not have permission to upload packages.');
        } else {
          throw new Error(`Failed to request upload URL: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      return this.normalizeUploadContract(data);
    } catch (error) {
      this.error = error.message || 'Failed to request upload URL';
      throw error;
    }
  }

  /**
   * Request presigned download URL
   * @param {string} packageName - Name of the package
   * @returns {Promise<Object>} - Presigned download URL and metadata
   */
  async requestPresignedDownload(packageName) {
    if (!this.isConfigured) {
      throw new Error('Package API is not configured. Please configure endpoint and bearer token.');
    }

    try {
      const response = await fetch(`${this.endpoint}/packages/presign-download`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          packageName
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please check your bearer token.');
        } else if (response.status === 403) {
          throw new Error('Access denied. You do not have permission to download packages.');
        } else if (response.status === 404) {
          throw new Error('Package not found on remote storage.');
        } else {
          throw new Error(`Failed to request download URL: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      return this.normalizeDownloadContract(data);
    } catch (error) {
      this.error = error.message || 'Failed to request download URL';
      throw error;
    }
  }

  /**
   * Delete remote package
   * @param {string} packageName - Name of the package to delete
   * @returns {Promise<boolean>} - Success status
   */
  async deleteRemotePackage(packageName) {
    if (!this.isConfigured) {
      throw new Error('Package API is not configured. Please configure endpoint and bearer token.');
    }

    try {
      const response = await fetch(`${this.endpoint}/packages/${encodeURIComponent(packageName)}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please check your bearer token.');
        } else if (response.status === 403) {
          throw new Error('Access denied. You do not have permission to delete packages.');
        } else if (response.status === 404) {
          throw new Error('Package not found on remote storage.');
        } else {
          throw new Error(`Failed to delete remote package: ${response.status} ${response.statusText}`);
        }
      }

      this.lastSync = new Date();
      return true;
    } catch (error) {
      this.error = error.message || 'Failed to delete remote package';
      throw error;
    }
  }

  /**
   * Upload package blob via pre-signed URL flow
   * @param {string} packageName - Name of package file
   * @param {Blob} packageBlob - ZIP blob
   * @param {(progress:number)=>void} onProgress - Optional progress callback
   * @returns {Promise<boolean>} - Success status
   */
  async uploadPackageBlob(packageName, packageBlob, onProgress = null) {
    if (!this.isConfigured) {
      throw new Error('Package API is not configured. Please configure endpoint and bearer token.');
    }

    try {
      if (onProgress) onProgress(5);

      const contract = await this.requestPresignedUpload(
        packageName,
        packageBlob.size,
        packageBlob.type || 'application/zip'
      );

      if (onProgress) onProgress(35);

      if (contract.method === 'POST' && contract.fields) {
        const formData = new FormData();
        Object.entries(contract.fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append('file', packageBlob, packageName);

        const uploadResponse = await fetch(contract.uploadUrl, {
          method: 'POST',
          body: formData
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }
      } else {
        const uploadHeaders = {
          ...(contract.headers || {}),
          'Content-Type': packageBlob.type || 'application/zip'
        };

        const uploadResponse = await fetch(contract.uploadUrl, {
          method: contract.method || 'PUT',
          headers: uploadHeaders,
          body: packageBlob
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }
      }

      if (onProgress) onProgress(100);

      this.lastSync = new Date();
      this.isConnected = true;
      this.error = null;
      return true;
    } catch (error) {
      this.isConnected = false;
      this.error = error.message || 'Failed to upload package';
      throw error;
    }
  }

  /**
   * Download package blob via pre-signed URL flow
   * @param {string} packageName - Name of package file
   * @param {(progress:number)=>void} onProgress - Optional progress callback
   * @returns {Promise<Blob>} - Downloaded ZIP blob
   */
  async downloadPackageBlob(packageName, onProgress = null) {
    if (!this.isConfigured) {
      throw new Error('Package API is not configured. Please configure endpoint and bearer token.');
    }

    try {
      if (onProgress) onProgress(5);

      const contract = await this.requestPresignedDownload(packageName);
      if (onProgress) onProgress(35);

      const response = await fetch(contract.downloadUrl, {
        method: 'GET',
        headers: contract.headers || {}
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();

      if (onProgress) onProgress(100);

      this.lastSync = new Date();
      this.isConnected = true;
      this.error = null;
      return blob;
    } catch (error) {
      this.isConnected = false;
      this.error = error.message || 'Failed to download package';
      throw error;
    }
  }

  /**
   * Get connection status
   * @returns {Object} - Connection status information
   */
  getConnectionStatus() {
    return {
      isConfigured: this.isConfigured,
      endpoint: this.endpoint,
      isConnected: this.isConnected,
      lastSync: this.lastSync,
      error: this.error
    };
  }

  /**
   * Clear error state
   */
  clearError() {
    this.error = null;
  }

  /**
   * Test connection to remote API
   * @returns {Promise<boolean>} - Connection test result
   */
  async testConnection() {
    if (!this.isConfigured) {
      return false;
    }

    try {
      const response = await fetch(`${this.endpoint}/packages`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      this.isConnected = response.ok;
      this.error = response.ok ? null : `Connection test failed: ${response.status}`;
      return response.ok;
    } catch (error) {
      this.isConnected = false;
      this.error = error.message || 'Connection test failed';
      return false;
    }
  }

  /**
   * Check if a field can be edited (not managed by environment)
   * @param {string} field - Field name ('endpoint' or 'token')
   * @returns {boolean} - True if field can be edited
   */
  canEditField(field) {
    if (field === 'endpoint') {
      return !this.isEndpointManaged;
    }
    if (field === 'token') {
      return !this.isTokenManaged;
    }
    return true;
  }
}

export default PackageApiService;
